/**
 * Contrat JSON d'entrée (webhook / HTTP) pour **importer une fiche talent** dans TalentHub.
 * **Agnostique du fournisseur** : tout ATS ou outil de sourcing qui pousse des profils vers le même endpoint
 * doit mapper son modèle vers ces types ; les noms n'impliquent aucun partenaire particulier.
 *
 * **Racine du document** : un seul objet {@link InboundTalentProfile} (pas d'enveloppe `payload` ni blocs annexes
 * certifications / pièces multiples / métadonnées génériques dans ce contrat).
 *
 * Champs absents du JSON : à mapper vers colonnes Prisma à `NULL` (ou défauts serveur) lors de l'ingestion.
 *
 * **CV** : contenu fichier en **base64** (optionnel), convention à aligner avec les autres flux Bloomays (ex. échanges existants avec un sourcing) ; stockage final côté ingestion → `Talent.cv`.
 *
 * Les types runtime sont dérivés de {@link InboundTalentProfileSchema} (Zod) pour éviter toute dérive entre validation et typage.
 *
 * Référence schéma interne : `packages/api.talents/src/assets/prisma/schema.prisma`.
 * Référence documentaire : `specs/TEC-2942-collective-payload-contract/talent-inbound.payload.ts`.
 */

import { z } from 'zod';

/**
 * Limite de taille du CV base64 : 10 Mo (cohérent avec `MAX_CV_SIZE_BYTES` dans `adapter.ats.collective/src/mappers/talent.mapper.ts`).
 * Dupliquée ici pour éviter un couplage de `types.shared` vers `adapter.ats.collective`.
 */
const MAX_CV_SIZE_BYTES = 10 * 1024 * 1024;

const dateYYYYMMSchema = z
  .string()
  .regex(/^\d{4}-\d{2}$/, 'Format date invalide : YYYY-MM attendu')
  .optional();

/** Aligné sur l'enum Prisma `Status` */
export const inboundDesiredContractTypeSchema = z.enum(['Freelance', 'CDI', 'FreeAndCDI']);
export type InboundDesiredContractType = z.infer<typeof inboundDesiredContractTypeSchema>;

/** Aligné sur l'enum Prisma `RemoteWork` */
export const inboundRemoteWorkPreferenceSchema = z.enum(['Flexible', 'Remote', 'OnSite']);
export type InboundRemoteWorkPreference = z.infer<typeof inboundRemoteWorkPreferenceSchema>;

/**
 * Intervalle de prix en **euros** (TJM ou salaire annuel selon l'usage sur le profil).
 * **Sans intervalle** : renseigner uniquement `minAmount`.
 * **Avec intervalle** : `minAmount` et `maxAmount` (borne haute optionnelle si non applicable).
 * Le mapping vers `Talent.tjm`, `desiredTjm`, `package`, `desiredPackage`, `packageVariable` est fait côté ingestion.
 */
export const inboundCompensationRangeSchema = z.object({
  minAmount: z.number(),
  maxAmount: z.number().optional(),
});
export type InboundCompensationRange = z.infer<typeof inboundCompensationRangeSchema>;

/**
 * Sous-ensemble `Location` pour l'inbound.
 * `region`, `longitude`, `latitude`, `range` : absents → `NULL` au mapping.
 */
export const inboundLocationSchema = z.object({
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string(),
  /** Code ISO 3166-1 alpha-2 (ex. FR, BE, DE) */
  countryCode: z.string().regex(/^[A-Z]{2}$/, 'countryCode doit être un code ISO 3166-1 alpha-2 (ex. FR, BE)'),
});
export type InboundLocation = z.infer<typeof inboundLocationSchema>;

/**
 * Lien de profil externe (ex. réseau professionnel). `Talent.url` peut être dérivé du lien principal convenu au mapping.
 */
export const inboundProfileLinkSchema = z.object({
  username: z.string().optional(),
  network: z.string(),
  url: z.string().url('url du profil doit être une URL valide'),
});
export type InboundProfileLink = z.infer<typeof inboundProfileLinkSchema>;

/** Référence d'intitulé ; `DirectJobTitle` est créé à partir de {@link InboundTalentProfile.jobTitle}. */
export interface InboundJobTitleRef {
  label: string;
}

/**
 * Une expérience mappée vers `Job` (+ `Period` pour les dates).
 * Convention : `colonnePrisma — nomAttributInbound` (la colonne est alimentée par l'attribut).
 *
 * - `name` — `companyName`
 * - `label` — `roleTitle`
 * - `description` — `summary` (optionnel)
 * - `Period.startDate` / `endDate` — `startDate` / `endDate` (optionnels, format YYYY-MM)
 *
 * Autres colonnes / relations `Job` (`url`, `jobTitle[]`, `skills`, etc.) : hors contrat pour l'instant → `NULL` ou non créées au mapping.
 */
export const inboundWorkExperienceSchema = z.object({
  /** `Job.name` — `companyName` */
  companyName: z.string(),
  /** `Job.label` — `roleTitle` */
  roleTitle: z.string(),
  /** `Job.description` — `summary` */
  summary: z.string().optional(),
  /** `Period.startDate` — format YYYY-MM recommandé */
  startDate: dateYYYYMMSchema,
  /** `Period.endDate` — format YYYY-MM recommandé */
  endDate: dateYYYYMMSchema,
});
export type InboundWorkExperience = z.infer<typeof inboundWorkExperienceSchema>;

/**
 * Mappé vers `Education` (+ `Period` pour les dates).
 * Convention : colonne Prisma — attribut inbound.
 *
 * - `name` (établissement / institution) — `name`
 * - `label` — `degreeName` (obligatoire dans ce contrat ; la colonne Prisma est encore nullable par erreur de schéma, non corrigée ici)
 * - `Period.startDate` / `endDate` — `startDate` / `endDate` (optionnels, format YYYY-MM)
 *
 * Autres colonnes (`description`, `url`, etc.) : hors contrat pour l'instant → `NULL` au mapping.
 */
export const inboundEducationSchema = z.object({
  /** `Education.name` — institution / établissement */
  name: z.string(),
  /** `Education.label` — diplôme, cursus ou domaine */
  degreeName: z.string(),
  /** `Period.startDate` — format YYYY-MM recommandé */
  startDate: dateYYYYMMSchema,
  /** `Period.endDate` — format YYYY-MM recommandé */
  endDate: dateYYYYMMSchema,
});
export type InboundEducation = z.infer<typeof inboundEducationSchema>;

/**
 * Langues : **libellé** obligatoire ; **niveau** optionnel, au format numérique TalentHub (`Language.level`).
 *
 * Chaque **émetteur** documente sa table de correspondance des libellés ou niveaux affichés chez lui vers ces nombres.
 * Exemple fréquent (à adapter par partenaire) : *Maternelle* → `1`, *Courant* → `0.8` ;
 * si aucun niveau n'est connu pour une langue, **omettre** `level`.
 */
export const inboundLanguageSchema = z.object({
  /** Nom de la langue (ex. « français », « Anglais », « Espagnol »). → `Language.label` */
  label: z.string(),
  /** → `Language.level` ; valeur entre 0 et 1 ; absent si l'émetteur n'a pas de niveau pour cette langue. */
  level: z.number().min(0).max(1).optional(),
});
export type InboundLanguage = z.infer<typeof inboundLanguageSchema>;

/**
 * Objet racine d'un envoi « fiche talent » depuis un ATS ou outil de sourcing.
 *
 * Champs retirés du contrat (absents du type) → colonnes / relations à `NULL` ou dérivés au mapping :
 * `name`, `positionTitle` / `DirectJobTitle` (dérivé de `jobTitle`), `label`, `owner`, `bloomerStatus`, `origin`,
 * `pipefyId`, `hiresweetId`, `airtableId`, `lastATSResumeRefresh`, `url` et URLs secondaires sur Talent sont à construire à partir de `profiles` si les règles d'import le prévoient.
 * Anciens scalaires `tjm`, `desiredTjm`, `package`, `desiredPackage`, `packageVariable` : remplacés par `tjm?` et `salary?` ({@link InboundCompensationRange}).
 * `legacyObjectID` et autres identifiants internes Bloomays : **hors contrat** ; l'émetteur ne fournit que {@link InboundTalentProfile.externalProfileId}.
 * `Talent.comment` : **hors contrat** (pas d'équivalent inbound).
 */
export const InboundTalentProfileSchema = z.object({
  /**
   * Identifiant stable du profil **côté système émetteur** (sourcing / ATS). **Obligatoire** : clé de corrélation et d'idempotence avec la fiche TalentHub.
   * Le mapping vers la colonne Prisma appropriée (`collectiveId`, clé dédiée à un autre fournisseur, etc.) est défini par l'intégration.
   */
  externalProfileId: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  jobTitle: z.string(),
  /** URL de la photo de profil. Doit être une URL valide (http ou https). → `Talent.image` */
  image: z.string().url('image doit être une URL valide (http ou https)').optional(),
  email: z.string().email('email invalide'),
  secondaryEmails: z.array(z.string().email()).optional(),
  phone: z.string().optional(),
  secondaryPhones: z.array(z.string()).optional(),
  summary: z.string().optional(),
  status: inboundDesiredContractTypeSchema.optional(),
  remoteWork: inboundRemoteWorkPreferenceSchema.optional(),
  /** TJM (€ / jour) : valeur seule ou fourchette. Remplace `tjm` / `desiredTjm` scalaires au mapping. */
  tjm: inboundCompensationRangeSchema.optional(),
  /** Rémunération annuelle (€ / an) : valeur seule ou fourchette. Remplace `package` / `desiredPackage` / `packageVariable` au mapping. */
  salary: inboundCompensationRangeSchema.optional(),
  availableNow: z.boolean().optional(),
  /**
   * Date à laquelle le provider a enregistré la disponibilité du talent (ISO 8601).
   * → mappé directement dans `Talent.availibilityUpdateDate`.
   *
   * Combiné avec `availableNow` :
   * - Si `availableNow=true` ET cette date est ≤ 60 jours → `Talent.availabilityDate = availabilityUpdateDate`, `Talent.availabilityDelay = 0`.
   * - Si `availableNow=true` ET cette date est > 60 jours → données trop anciennes, `availabilityDate` / `availabilityDelay` non sauvegardés.
   */
  availabilityUpdateDate: z.string().optional(),
  /** Si `Talent.locationId` est null, omettre ce bloc. */
  location: inboundLocationSchema.optional(),
  profiles: z.array(inboundProfileLinkSchema).optional(),
  /**
   * Libellés de compétences / tags côté émetteur.
   * Mapping ultérieur vers `Skill` / `DirectSkill` / `SkillTalentLevel` selon les règles métier.
   */
  skills: z.array(z.string()).optional(),
  /**
   * CV : fichier encodé en **base64** (optionnel). Décodage et stockage côté ingestion → `Talent.cv`.
   */
  cv: z
    .string()
    .refine(
      (val) => Buffer.from(val, 'base64').byteLength <= MAX_CV_SIZE_BYTES,
      `CV dépasse la taille maximale autorisée (${MAX_CV_SIZE_BYTES / 1024 / 1024} Mo)`,
    )
    .optional(),
  work: z.array(inboundWorkExperienceSchema).optional(),
  education: z.array(inboundEducationSchema).optional(),
  languages: z.array(inboundLanguageSchema).optional(),
});

export type InboundTalentProfile = z.infer<typeof InboundTalentProfileSchema>;
